import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Upload, Download, FileSpreadsheet, FileText, FileDown, ArrowRight, ArrowLeftRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import ImportacaoCSVModal from '../components/ImportacaoCSVModal';

const FORMATS = [
  { id: 'csv', label: 'CSV', desc: 'Planilhas e outros sistemas', icon: FileDown, ext: 'csv', tone: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400' },
  { id: 'excel', label: 'Excel', desc: 'Arquivo .xlsx formatado', icon: FileSpreadsheet, ext: 'xlsx', tone: 'bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400' },
  { id: 'pdf', label: 'PDF', desc: 'Relatório para imprimir', icon: FileText, ext: 'pdf', tone: 'bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-400' },
];

export default function ImportarExportar() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const [showImport, setShowImport] = useState(false);
  const [filters, setFilters] = useState({ data_inicio: '', data_fim: '', tipo: '' });
  const [exporting, setExporting] = useState(null);

  const setF = (k, v) => setFilters(p => ({ ...p, [k]: v }));

  const handleExport = async (fmt) => {
    setExporting(fmt.id);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await api.get(`/relatorios/export/${fmt.id}?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `lancamentos.${fmt.ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exportação ${fmt.label} concluída!`);
    } catch {
      toast.error('Erro ao exportar. Tente novamente.');
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto space-y-5 page-enter pb-16" data-testid="importar-exportar-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2.5" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          <span className="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shadow-sm">
            <ArrowLeftRight size={20} />
          </span>
          Importar e exportar
        </h1>
        <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Traga seus lançamentos de outra planilha ou leve seus dados para fora do Gastify
        </p>
      </div>

      {/* Importar */}
      <div className="fin-card p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <span className="w-12 h-12 rounded-2xl bg-indigo-100 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-400 flex items-center justify-center flex-shrink-0">
          <Upload size={22} />
        </span>
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100">Importar lançamentos</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Envie um arquivo CSV com receitas e despesas. Você confere a prévia antes de confirmar e pode corrigir linhas com erro.
          </p>
        </div>
        <button
          onClick={() => setShowImport(true)}
          className="nexus-btn-primary inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold"
          data-testid="btn-importar-csv"
        >
          Importar CSV ou PDF <ArrowRight size={15} />
        </button>
      </div>

      {/* Exportar */}
      <div className="fin-card p-5 sm:p-6 space-y-4">
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
            <Download size={22} />
          </span>
          <div>
            <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100">Exportar lançamentos</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Escolha o período (opcional) e o formato do arquivo.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="fin-label">Data início</label>
            <input type="date" value={filters.data_inicio} onChange={e => setF('data_inicio', e.target.value)} className="fin-input w-full" />
          </div>
          <div>
            <label className="fin-label">Data fim</label>
            <input type="date" value={filters.data_fim} onChange={e => setF('data_fim', e.target.value)} className="fin-input w-full" />
          </div>
          <div>
            <label className="fin-label">Tipo</label>
            <select value={filters.tipo} onChange={e => setF('tipo', e.target.value)} className="fin-input w-full">
              <option value="">Todos</option>
              <option value="RECEITA">Receitas</option>
              <option value="DESPESA">Despesas</option>
              <option value="CUSTO">Custos</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {FORMATS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => handleExport(f)}
              disabled={!!exporting}
              data-testid={`export-${f.id}`}
              className="text-left p-4 rounded-2xl border border-slate-200/80 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors flex items-center gap-3 disabled:opacity-60 cursor-pointer"
            >
              <span className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${f.tone}`}><f.icon size={18} /></span>
              <span className="min-w-0">
                <span className="block text-sm font-bold text-slate-800 dark:text-slate-100">{exporting === f.id ? 'Gerando…' : `Exportar ${f.label}`}</span>
                <span className="block text-[11px] text-slate-400">{f.desc}</span>
              </span>
            </button>
          ))}
        </div>
        <p className="text-[11px] text-slate-400">
          Quer relatórios com gráficos e análises? Veja a tela de{' '}
          <button className="font-semibold text-indigo-500 hover:underline" onClick={() => navigate(`/${tenantSlug}/relatorios`)}>Relatórios</button>.
        </p>
      </div>

      <ImportacaoCSVModal open={showImport} onClose={() => setShowImport(false)} onImported={() => toast.success('Lançamentos importados!')} />
    </div>
  );
}
